import type { CookieAttributes } from 'js-cookie';

import { isDev } from '@/legacy/utils/env';
import { tlpBase } from '@/utils/gateway';

const isIpOrUnqualified = (host: string): boolean => {
  if (!host.includes('.')) return true;
  if (host.includes(':')) return true;
  if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) return true;
  return false;
};

// Longest shared suffix of two hostnames, requiring at least two labels (e.g.
// `timelog.com`). Returns `undefined` when there is no meaningful shared
// parent — equal hosts, IPs, `localhost`, or no common suffix — so the cookie
// stays host-only and the browser does the right thing in dev.
export const getSharedCookieDomain = (
  frontendHost: string,
  tlpHost: string,
): string | undefined => {
  if (!frontendHost || !tlpHost) return undefined;
  const f = frontendHost.toLowerCase();
  const t = tlpHost.toLowerCase();
  if (f === t) return undefined;
  if (isIpOrUnqualified(f) || isIpOrUnqualified(t)) return undefined;

  const fLabels = f.split('.');
  const tLabels = t.split('.');
  const common: string[] = [];
  while (
    fLabels.length &&
    tLabels.length &&
    fLabels[fLabels.length - 1] === tLabels[tLabels.length - 1]
  ) {
    common.unshift(fLabels.pop() as string);
    tLabels.pop();
  }
  if (common.length < 2) return undefined;
  return common.join('.');
};

const hostFromUrl = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  try {
    return new URL(url).hostname;
  } catch {
    return undefined;
  }
};

// Cookie options for the JWT mirror cookies (`accessToken`, `accessTokenPhoenix`).
// In production the frontend and TLP live on different subdomains of the same
// registrable domain (e.g. `app.timelog.com` and `app5.timelog.com`); setting
// `domain` to the shared parent makes the browser send the cookie to both. In
// dev the local TLP proxy answers on `localhost` so the host-only default is
// correct and we leave `domain` unset.
//
// `tlpUrl` may be passed explicitly (e.g. from the OIDC `app_url` claim at
// sign-in time, before `setAppUrlFromToken` has run). When omitted the helper
// falls back to `tlpBase()`.
export const getAuthCookieOptions = (tlpUrl?: string): CookieAttributes => {
  const frontendHost =
    typeof window !== 'undefined' ? window.location.hostname : '';
  const tlpHost = hostFromUrl(tlpUrl) ?? hostFromUrl(tlpBase());
  const domain = tlpHost
    ? getSharedCookieDomain(frontendHost, tlpHost)
    : undefined;

  const options = {
    path: '/',
    sameSite: 'strict',
    secure: !isDev,
    ...(domain ? { domain } : {}),
  };
  const dd = getSharedCookieDomain(
    'test-login.test.cluster.timelog.com',
    hostFromUrl('https://tlp.test-login.test.cluster.timelog.com') ?? '',
  );
  console.log('shared_domain', domain, dd);
  console.log(
    'Setting with cookie options',
    options,
    tlpUrl,
    window.location.hostname,
    dd,
  );
  return options;
};
